import { Observable, merge } from "rxjs";
import { filter, map, share } from "rxjs/operators/index.js";

export function gestureDelta(before, after) {
    if (before.length !== after.length || !after.length) return null;
    if (after.length === 1) return { x: after[0].x-before[0].x, y: after[0].y-before[0].y };
    const center = points => ({ x: (points[0].x+points[1].x)/2, y: (points[0].y+points[1].y)/2 });
    const distance = points => Math.hypot(points[0].x-points[1].x, points[0].y-points[1].y);
    const a = center(before), b = center(after);
    return { x: b.x-a.x, y: b.y-a.y, zoom: (distance(before)-distance(after))*0.1 };
}

export function embedInput(canvas) {
    const events = new Observable(subscriber => {
        const points = new Map();
        let mode = "orbit";
        const down = event => {
            if (event.pointerType === "touch") return;
            event.preventDefault();
            canvas.setPointerCapture(event.pointerId);
            mode = event.button === 2 ? "zoom" : event.button === 1 || event.shiftKey ? "pan" : "orbit";
            points.set(event.pointerId, { x: event.clientX, y: event.clientY });
        };
        const move = event => {
            if (!points.has(event.pointerId)) return;
            const before = [...points.values()];
            points.set(event.pointerId, { x: event.clientX, y: event.clientY });
            const delta = gestureDelta(before, [...points.values()]);
            if (!delta || points.size > 2) return;
            if (points.size === 2) {
                subscriber.next({ type: "pan", value: { deltaX: delta.x, deltaY: delta.y } });
                subscriber.next({ type: "zoom", value: { deltaZoom: delta.zoom } });
            } else if (mode === "orbit") subscriber.next({ type: "orbit", value: { deltaPhi: delta.x, deltaTheta: delta.y } });
            else if (mode === "pan") subscriber.next({ type: "pan", value: { deltaX: delta.x, deltaY: delta.y } });
            else subscriber.next({ type: "zoom", value: { deltaZoom: delta.y } });
        };
        const up = event => points.delete(event.pointerId);
        const menu = event => event.preventDefault();
        const handlers = { pointerdown: down, pointermove: move, pointerup: up, pointercancel: up, lostpointercapture: up, contextmenu: menu };
        for (const [name, handler] of Object.entries(handlers)) canvas.addEventListener(name, handler, { passive: false });
        return () => { for (const [name, handler] of Object.entries(handlers)) canvas.removeEventListener(name, handler); };
    }).pipe(share());
    const touch = touchInput(canvas);
    const stream = type => merge(events.pipe(filter(event => event.type === type), map(event => event.value)), touch[type]);
    return { orbit: stream("orbit"), pan: stream("pan"), zoom: stream("zoom") };
}

// Use non-passive Touch Events for Safari; touch pointers are excluded above
// so browsers exposing both event families never apply the gesture twice.
export function touchInput(canvas) {
    const events = new Observable(subscriber => {
        let previous = [];
        const read = event => Array.from(event.targetTouches).map(t => ({id: t.identifier, x: t.clientX, y: t.clientY})).sort((a,b) => a.id-b.id);
        const reset = event => {
            if (event.cancelable) event.preventDefault();
            previous = read(event);
        };
        const move = event => {
            if (event.cancelable) event.preventDefault();
            const current = read(event);
            const sameTouches = previous.length === current.length && current.every((point,i) => point.id === previous[i].id);
            const delta = sameTouches && current.length <= 2 ? gestureDelta(previous, current) : null;
            previous = current;
            if (!delta) return;
            if (current.length === 1) subscriber.next({type: "orbit", value: {deltaPhi: delta.x, deltaTheta: delta.y}});
            else {
                subscriber.next({type: "pan", value: {deltaX: delta.x, deltaY: delta.y}});
                subscriber.next({type: "zoom", value: {deltaZoom: delta.zoom}});
            }
        };
        const handlers = {touchstart: reset, touchmove: move, touchend: reset, touchcancel: reset};
        for (const [name, handler] of Object.entries(handlers)) canvas.addEventListener(name, handler, {passive: false});
        return () => { for (const [name, handler] of Object.entries(handlers)) canvas.removeEventListener(name, handler); };
    }).pipe(share());
    const stream = type => events.pipe(filter(event => event.type === type), map(event => event.value));
    return { orbit: stream("orbit"), pan: stream("pan"), zoom: stream("zoom") };
}
